import { PLAN_BULK_MAX_IDS, type PlanDto } from '@planuze/admin-contracts';
import { Button, ConfirmDialog, InlineAlert, TableSkeleton, toast } from '@planuze/ui';
import { useMemo, useState } from 'react';
import { useAdminLocale } from '../../content/locale-provider.js';
import { usePlansContent } from '../../content/sections/plans.js';
import { useArchivePlan, useBulkArchivePlans, usePlansList } from '../../hooks/use-plans.js';
import { FacetMultiSelect } from '../filters/facet-multi-select.js';
import { SegmentedControl } from '../filters/segmented-control.js';
import { SearchInput } from '../search-input.js';
import { ArchivePlanDialog } from './archive-plan-dialog.js';
import { PlanBulkBar } from './plan-bulk-bar.js';
import { PlanEditModal } from './plan-edit-modal.js';
import { planErrorMessage } from './plan-error.js';
import { matchesQuery } from './plan-search.js';
import { PublicPlansTable } from './public-plans-table.js';

/* Aba Planos (públicos): CRUD pelo painel (ADR-0419). A lista é paginada por cursor (ADR-0713);
   os filtros são client-side (ADR-0704) sobre as PÁGINAS JÁ CARREGADAS — busca + tier (faceta) +
   visibilidade + intervalo + status. Como a lista é paginada, os filtros e contadores só enxergam
   o que já foi carregado; "Carregar mais" traz a próxima página. `canManage` (super_admin/
   billing_ops) habilita editar/arquivar; a criação é uma página cheia (plan-create-page),
   alcançada pelo botão do header. A edição segue em modal (PlanEditModal). */

type Visibility = 'all' | 'public' | 'hidden';
type IntervalFilter = 'all' | 'month' | 'year';
/* Filtro por status do catálogo (ADR-0419). `active` é o default — arquivados só aparecem
   quando pedidos, preservando a semântica de "arquivar = tirar da vista". */
type StatusFilter = 'all' | 'active' | 'archived';

export function PublicSection({
  active,
  canManage,
}: {
  readonly active: boolean;
  readonly canManage: boolean;
}) {
  const copy = usePlansContent();
  const { locale } = useAdminLocale();
  /* Lazy (ADR-0704): a query só dispara quando a aba está ativa. */
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    usePlansList(active);
  const archive = useArchivePlan();
  const bulkArchive = useBulkArchivePlans();
  const [editing, setEditing] = useState<PlanDto | null>(null);
  const [toArchive, setToArchive] = useState<PlanDto | null>(null);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [pendingBulkIds, setPendingBulkIds] = useState<readonly string[] | null>(null);
  const [search, setSearch] = useState('');
  const [tiers, setTiers] = useState<string[]>([]);
  const [visibility, setVisibility] = useState<Visibility>('all');
  const [billingInterval, setBillingInterval] = useState<IntervalFilter>('all');
  const [status, setStatus] = useState<StatusFilter>('active');

  const confirmArchive = (): void => {
    if (toArchive === null) return;
    archive.mutate(toArchive.id, {
      onSuccess: () => {
        toast.success(copy.publicSection.archivedToast);
        setToArchive(null);
      },
      onError: (err) => toast.error(planErrorMessage(err, locale)),
    });
  };

  const plans = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);
  const tierOptions = useMemo(
    () =>
      Array.from(new Set(plans.map((p) => p.tier)))
        .sort((a, b) => a.localeCompare(b))
        .map((t) => ({ value: t, label: t })),
    [plans],
  );

  const query = search.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      plans.filter((plan) => {
        if (query !== '' && !matchesQuery(plan, query)) return false;
        if (tiers.length > 0 && !tiers.includes(plan.tier)) return false;
        if (visibility === 'public' && !plan.isPublic) return false;
        if (visibility === 'hidden' && plan.isPublic) return false;
        if (billingInterval !== 'all' && plan.interval !== billingInterval) return false;
        if (status !== 'all' && plan.status !== status) return false;
        return true;
      }),
    [plans, query, tiers, visibility, billingInterval, status],
  );
  /* A seleção efetiva é sempre a interseção entre a tabela visível e planos ativos. Um plano
     filtrado/arquivado depois da seleção não pode entrar silenciosamente no lote. */
  const eligiblePlans = useMemo(
    () => filtered.filter((plan) => plan.status === 'active'),
    [filtered],
  );
  const selectedPlans = useMemo(
    () => eligiblePlans.filter((plan) => selectedIds.has(plan.id)),
    [eligiblePlans, selectedIds],
  );

  const toggle = (id: string): void =>
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else if (next.size < PLAN_BULK_MAX_IDS) next.add(id);
      return next;
    });

  const toggleAll = (checked: boolean): void =>
    setSelectedIds(
      checked
        ? new Set(eligiblePlans.slice(0, PLAN_BULK_MAX_IDS).map((plan) => plan.id))
        : new Set(),
    );

  const hasFilters =
    query !== '' ||
    tiers.length > 0 ||
    visibility !== 'all' ||
    billingInterval !== 'all' ||
    status !== 'active';
  const clearFilters = (): void => {
    setSearch('');
    setTiers([]);
    setVisibility('all');
    setBillingInterval('all');
    setStatus('active');
  };

  /* Contadores por opção computados sobre a lista carregada (ADR-0704). */
  const visibilityCounts = useMemo(
    () => ({
      all: plans.length,
      public: plans.filter((p) => p.isPublic).length,
      hidden: plans.filter((p) => !p.isPublic).length,
    }),
    [plans],
  );
  const intervalCounts = useMemo(
    () => ({
      all: plans.length,
      month: plans.filter((p) => p.interval === 'month').length,
      year: plans.filter((p) => p.interval === 'year').length,
    }),
    [plans],
  );
  const statusCounts = useMemo(
    () => ({
      all: plans.length,
      active: plans.filter((p) => p.status === 'active').length,
      archived: plans.filter((p) => p.status === 'archived').length,
    }),
    [plans],
  );

  return (
    <div className="space-y-4">
      <InlineAlert variant="info">{copy.publicSection.intro}</InlineAlert>
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          value={search}
          onChange={setSearch}
          ariaLabel={copy.publicSection.searchAriaLabel}
          placeholder={copy.common.searchPlaceholder}
        />
        <FacetMultiSelect
          label={copy.common.tierFilterLabel}
          values={tiers}
          onChange={setTiers}
          options={tierOptions}
        />
        <SegmentedControl<Visibility>
          value={visibility}
          onChange={setVisibility}
          ariaLabel={copy.publicSection.visibilityFilterAriaLabel}
          options={[
            { value: 'all', label: copy.common.all, count: visibilityCounts.all },
            {
              value: 'public',
              label: copy.publicSection.visibilityPublic,
              count: visibilityCounts.public,
            },
            {
              value: 'hidden',
              label: copy.publicSection.visibilityHidden,
              count: visibilityCounts.hidden,
            },
          ]}
        />
        <SegmentedControl<IntervalFilter>
          value={billingInterval}
          onChange={setBillingInterval}
          ariaLabel={copy.publicSection.intervalFilterAriaLabel}
          options={[
            { value: 'all', label: copy.common.all, count: intervalCounts.all },
            { value: 'month', label: copy.common.intervalMonthly, count: intervalCounts.month },
            { value: 'year', label: copy.common.intervalYearly, count: intervalCounts.year },
          ]}
        />
        <SegmentedControl<StatusFilter>
          value={status}
          onChange={setStatus}
          ariaLabel={copy.common.statusFilterAriaLabel}
          options={[
            { value: 'all', label: copy.common.all, count: statusCounts.all },
            { value: 'active', label: copy.publicSection.statusActive, count: statusCounts.active },
            {
              value: 'archived',
              label: copy.publicSection.statusArchived,
              count: statusCounts.archived,
            },
          ]}
        />
      </div>
      {canManage && (
        <PlanBulkBar
          selectedCount={selectedPlans.length}
          busy={bulkArchive.isPending}
          onArchive={() =>
            setPendingBulkIds(selectedPlans.slice(0, PLAN_BULK_MAX_IDS).map((plan) => plan.id))
          }
          onClear={() => setSelectedIds(new Set())}
        />
      )}
      {isError ? (
        <InlineAlert variant="destructive">{copy.publicSection.loadError}</InlineAlert>
      ) : isLoading || data === undefined ? (
        <TableSkeleton columns={canManage ? 9 : 7} />
      ) : (
        <PublicPlansTable
          plans={filtered}
          canManage={canManage}
          onEdit={setEditing}
          onArchive={setToArchive}
          hasFilters={hasFilters}
          onClearFilters={clearFilters}
          selectedIds={selectedIds}
          onToggle={toggle}
          onToggleAll={toggleAll}
        />
      )}
      {hasNextPage && (
        <div className="flex justify-center">
          <Button
            variant="ghost"
            loading={isFetchingNextPage}
            onClick={() => {
              fetchNextPage().catch(() => undefined);
            }}
          >
            {copy.common.loadMore}
          </Button>
        </div>
      )}
      {editing !== null && <PlanEditModal open plan={editing} onClose={() => setEditing(null)} />}
      <ArchivePlanDialog
        open={toArchive !== null}
        onOpenChange={(next) => {
          if (!next) setToArchive(null);
        }}
        isPending={archive.isPending}
        title={copy.publicSection.archiveDialogTitle}
        description={copy.publicSection.archiveDialogDescription(toArchive?.displayName ?? '')}
        onConfirm={confirmArchive}
      />
      <ConfirmDialog
        open={pendingBulkIds !== null}
        onOpenChange={(open) => {
          if (!open) setPendingBulkIds(null);
        }}
        title={copy.publicSection.bulkTitle}
        description={copy.publicSection.bulkDescription(pendingBulkIds?.length ?? 0)}
        confirmLabel={copy.publicSection.bulkConfirm}
        variant="destructive"
        mapError={(error) => planErrorMessage(error, locale)}
        onConfirm={async () => {
          if (pendingBulkIds === null || pendingBulkIds.length === 0) return;
          const response = await bulkArchive.mutateAsync(pendingBulkIds);
          const failed = response.results.filter((result) => !result.ok);
          if (failed.length === 0) {
            toast.success(copy.publicSection.bulkSuccess(response.results.length));
          } else {
            toast.warning(
              copy.publicSection.bulkPartial(
                response.results.length - failed.length,
                failed.length,
              ),
            );
          }
          setSelectedIds(new Set(failed.map((result) => result.id)));
          setPendingBulkIds(null);
        }}
      />
    </div>
  );
}
